import { useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";
import { api, fullUrl } from "../lib/api";
import { SERVICES, SUBURBS, PROPERTY_TYPES } from "../data/site";

const initial = {
  name: "",
  phone: "",
  email: "",
  suburb: "",
  service: "",
  property_type: "",
  message: "",
  request_callback: false,
  photo_urls: [],
};

export default function QuoteForm({ variant = "light", compact = false, sourcePage }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const onDark = variant === "dark";
  const fieldCls = onDark ? "field-dark" : "field";
  const labelCls = onDark ? "field-label" : "field-label";
  const labelStyle = onDark ? { color: "rgba(255,255,255,0.65)" } : {};

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      update("photo_urls", [...form.photo_urls, ...urls]);
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error("Photo upload failed. You can still submit without photos.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (url) => {
    update("photo_urls", form.photo_urls.filter((u) => u !== url));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please add your name, phone and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source_page: sourcePage || window.location.pathname });
      setDone(true);
      setForm(initial);
      toast.success("Quote request sent — we'll be in touch within one business day.");
      // Conversion tracking placeholders
      if (typeof window !== "undefined") {
        try { window.gtag && window.gtag("event", "generate_lead", { event_category: "form", event_label: "quote" }); } catch (_) {}
        try { window.fbq && window.fbq("track", "Lead"); } catch (_) {}
      }
    } catch (err) {
      toast.error("We couldn't send your request. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className={`text-center py-12 ${onDark ? "text-white" : "text-[#0B1426]"}`} data-testid="quote-success">
        <div className="hairline-gold mx-auto mb-6" />
        <h3 className="font-display text-3xl md:text-4xl">Thank you.</h3>
        <p className={`mt-4 max-w-md mx-auto ${onDark ? "text-white/70" : "text-[#4F5B6A]"}`}>
          Your quote request has been received. A member of our team will be in touch within one business day.
        </p>
        <button className="mt-8 btn-secondary" onClick={() => setDone(false)} data-testid="quote-submit-another"
                style={onDark ? { color: "#fff", borderColor: "rgba(255,255,255,0.4)" } : {}}>
          Submit another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
      <div>
        <label className={labelCls} style={labelStyle}>Full Name *</label>
        <input className={fieldCls} value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="quote-name" />
      </div>
      <div>
        <label className={labelCls} style={labelStyle}>Phone *</label>
        <input className={fieldCls} type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} required data-testid="quote-phone" />
      </div>
      <div>
        <label className={labelCls} style={labelStyle}>Email *</label>
        <input className={fieldCls} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required data-testid="quote-email" />
      </div>
      <div>
        <label className={labelCls} style={labelStyle}>Suburb</label>
        <select className={fieldCls} value={form.suburb} onChange={(e) => update("suburb", e.target.value)} data-testid="quote-suburb"
                style={onDark ? { backgroundColor: "transparent", color: "#fff" } : {}}>
          <option value="">Select suburb…</option>
          {SUBURBS.map((s) => <option key={s.slug} value={s.name} style={{ color: "#0B1426" }}>{s.name}</option>)}
          <option value="Other" style={{ color: "#0B1426" }}>Other Sunshine Coast</option>
        </select>
      </div>
      <div>
        <label className={labelCls} style={labelStyle}>Service needed</label>
        <select className={fieldCls} value={form.service} onChange={(e) => update("service", e.target.value)} data-testid="quote-service"
                style={onDark ? { backgroundColor: "transparent", color: "#fff" } : {}}>
          <option value="">Select service…</option>
          {SERVICES.map((s) => <option key={s.slug} value={s.name} style={{ color: "#0B1426" }}>{s.name}</option>)}
        </select>
      </div>
      <div>
        <label className={labelCls} style={labelStyle}>Property type</label>
        <select className={fieldCls} value={form.property_type} onChange={(e) => update("property_type", e.target.value)} data-testid="quote-property-type"
                style={onDark ? { backgroundColor: "transparent", color: "#fff" } : {}}>
          <option value="">Select type…</option>
          {PROPERTY_TYPES.map((t) => <option key={t} value={t} style={{ color: "#0B1426" }}>{t}</option>)}
        </select>
      </div>

      {!compact && (
        <div className="md:col-span-2">
          <label className={labelCls} style={labelStyle}>Tell us about the job</label>
          <textarea className={fieldCls} rows={3} value={form.message} onChange={(e) => update("message", e.target.value)}
                    placeholder="Rough scope, colour ideas, deadlines…" data-testid="quote-message" />
        </div>
      )}

      <div className="md:col-span-2">
        <label className={labelCls} style={labelStyle}>Photos (optional — helps us quote accurately)</label>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <label className={`inline-flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-wider cursor-pointer border ${onDark ? "border-white/25 text-white/85 hover:border-[#C5A059]" : "border-[#0B1426]/30 text-[#0B1426] hover:border-[#C5A059]"}`}>
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            <span>{uploading ? "Uploading…" : "Add photos"}</span>
            <input type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} data-testid="quote-photo-input" />
          </label>
          {form.photo_urls.map((u) => (
            <div key={u} className="relative w-16 h-16 border border-[#E5E3DB]" data-testid="quote-photo-thumb">
              <img src={fullUrl(u)} alt="upload" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removePhoto(u)} className="absolute -top-2 -right-2 bg-[#0B1426] text-white p-0.5 rounded-full" aria-label="Remove">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="md:col-span-2 flex items-center gap-2">
        <input id="callback" type="checkbox" checked={form.request_callback}
               onChange={(e) => update("request_callback", e.target.checked)}
               className="w-4 h-4 accent-[#C5A059]" data-testid="quote-callback" />
        <label htmlFor="callback" className={`text-sm ${onDark ? "text-white/80" : "text-[#4F5B6A]"}`}>
          Request a callback — we'll phone you back within one business hour.
        </label>
      </div>

      <div className="md:col-span-2">
        <button type="submit" disabled={submitting} className="btn-gold w-full md:w-auto" data-testid="quote-submit">
          {submitting ? "Sending…" : "Send Quote Request"}
        </button>
        <p className={`mt-4 text-xs ${onDark ? "text-white/55" : "text-[#4F5B6A]"}`}>
          We respond to all enquiries within one business day. Your details are private and never shared.
        </p>
      </div>
    </form>
  );
}
